/**
 * PerProductResolutionPicker — Wave 6.3 per-account disposition picker.
 *
 * Renders a row per product when a cart has multiple products AND the FSR
 * needs to record a divergent disposition for any one of them (e.g. 2 of 3
 * accounts succeeded but a CD hit a System-Error on Core Director timeout).
 *
 * Defaults: cart-level code applied to all rows. FSR can override per row.
 * Submit fires `client.retail.carts.productResolution.upsert(...)` for each row
 * where the disposition differs from the cart-level default.
 *
 * Auto-collapse: if the cart has 0 or 1 products, this component returns null —
 * the cart-level dropdown alone is sufficient, no per-row UI is needed.
 */

import { useEffect, useMemo, useState } from 'react';
import type {
  CartDto,
  CartProductResolutionInput,
  CartResolutionCode,
  Product,
  SystemErrorCode,
} from '@retail-ops/shared-types';
import { Pill } from '@/components/primitives';
import { getValidResolutionOptions, humanizeOriginTier } from '../utils/cartResolutionMatrix';

export interface PerProductResolutionPickerProps {
  cart: CartDto;
  /** Resolved Product catalog rows so the row label is humanized. */
  products: ReadonlyArray<Product>;
  /** The cart-level code chosen in the parent CloseCartModal. */
  cartLevelCode: CartResolutionCode | null;
  /** The cart-level errorCode chosen if cartLevelCode === 'System-Error'. */
  cartLevelErrorCode?: string;
  /** SystemErrorCodes catalog (loaded once by the parent modal). */
  errorCodes: ReadonlyArray<SystemErrorCode>;
  /**
   * Current divergent rows. Parent owns state so it can submit them on save.
   * Pass an empty array on first render — picker self-initializes from the
   * cart's product list.
   */
  rows: ReadonlyArray<CartProductResolutionInput>;
  onChange: (rows: ReadonlyArray<CartProductResolutionInput>) => void;
}

interface CartProductRow {
  productCode: string;
  productName: string;
}

/** Read products out of the cart's `productsJson` payload. */
function readCartProducts(
  cart: CartDto,
  catalog: ReadonlyArray<Product>,
): ReadonlyArray<CartProductRow> {
  if (!cart.productsJson) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(cart.productsJson);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((p): CartProductRow | null => {
      if (!p || typeof p !== 'object') return null;
      const obj = p as Record<string, unknown>;
      const code =
        typeof obj.productCode === 'string'
          ? obj.productCode
          : typeof obj.code === 'string'
            ? obj.code
            : null;
      if (!code) return null;
      const catalogRow = catalog.find((c) => c.code === code);
      const name =
        typeof obj.productName === 'string'
          ? obj.productName
          : catalogRow?.name ?? code;
      return { productCode: code, productName: name };
    })
    .filter((r): r is CartProductRow => r !== null);
}

export function PerProductResolutionPicker({
  cart,
  products,
  cartLevelCode,
  cartLevelErrorCode,
  errorCodes,
  rows,
  onChange,
}: PerProductResolutionPickerProps): JSX.Element | null {
  const cartProducts = useMemo(() => readCartProducts(cart, products), [cart, products]);
  const [expanded, setExpanded] = useState<boolean>(false);

  // Re-seed local state when the cart-level code changes (e.g., FSR flipped
  // the radio in the parent modal). All rows default to the cart-level code.
  useEffect(() => {
    if (cartProducts.length <= 1) return;
    const seeded: CartProductResolutionInput[] = cartProducts.map((p) => {
      const base: CartProductResolutionInput = {
        productCode: p.productCode,
        resolutionCode: cartLevelCode ?? 'Open',
      };
      if (cartLevelCode === 'System-Error' && cartLevelErrorCode) {
        base.errorCode = cartLevelErrorCode;
      }
      return base;
    });
    onChange(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.cartId, cartLevelCode, cartLevelErrorCode, cartProducts.length]);

  if (cartProducts.length <= 1) return null;

  const validOptions = getValidResolutionOptions(cart.originRole, cart.hasRegulatedArtifact);

  function updateRow(idx: number, patch: Partial<CartProductResolutionInput>): void {
    const next = rows.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange(next);
  }

  // Count how many rows diverge from the cart-level default — used in the
  // collapse summary so the FSR knows how many overrides they have.
  const divergeCount = rows.filter((r) => r.resolutionCode !== cartLevelCode).length;

  return (
    <div
      style={{
        marginTop: 'var(--sp-5)',
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-md)',
        background: 'var(--surface-card)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: 'var(--sp-4) var(--sp-5)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--ink-900)',
          fontSize: 13,
          fontWeight: 600,
          textAlign: 'left',
        }}
        aria-expanded={expanded ? 'true' : 'false'}
      >
        <span>
          Per-product disposition{' '}
          <span style={{ color: 'var(--ink-600)', fontWeight: 400 }}>
            ({cartProducts.length} products
            {divergeCount > 0 ? ` · ${divergeCount} override${divergeCount === 1 ? '' : 's'}` : ''}
            )
          </span>
        </span>
        <span aria-hidden style={{ fontSize: 11, color: 'var(--ink-500)' }}>
          {expanded ? '▾ collapse' : '▸ expand'}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5 }}>
            Each product defaults to the cart-level code{' '}
            {cartLevelCode && <Pill variant="info">{cartLevelCode}</Pill>}. Override any row that
            should record a different disposition (e.g. 2 of 3 accounts succeeded but the third
            hit a system error). Origin tier:{' '}
            <Pill variant="muted">{humanizeOriginTier(cart.originRole)}</Pill>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ink-100)', textAlign: 'left' }}>
                <th style={th()}>Product</th>
                <th style={th()}>Disposition</th>
                <th style={th()}>Error code</th>
                <th style={th()}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {cartProducts.map((p, idx) => {
                const row = rows[idx] ?? {
                  productCode: p.productCode,
                  resolutionCode: cartLevelCode ?? 'Open',
                };
                const isSE = row.resolutionCode === 'System-Error';
                const isOverride = row.resolutionCode !== cartLevelCode;
                return (
                  <tr
                    key={p.productCode}
                    style={{
                      borderBottom: '1px solid var(--surface-border)',
                      background: isOverride ? 'var(--info-bg)' : undefined,
                    }}
                  >
                    <td style={td()}>
                      <div style={{ fontWeight: 600, color: 'var(--ink-900)' }}>{p.productName}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>{p.productCode}</div>
                    </td>
                    <td style={td()}>
                      <select
                        aria-label={`Disposition for ${p.productName}`}
                        title={`Disposition for ${p.productName}`}
                        value={row.resolutionCode}
                        onChange={(e) =>
                          updateRow(idx, {
                            resolutionCode: e.target.value as CartResolutionCode,
                            // Clear errorCode when switching off System-Error.
                            errorCode:
                              e.target.value === 'System-Error' ? row.errorCode : undefined,
                          })
                        }
                        style={selectStyle()}
                      >
                        {validOptions.map((opt) => (
                          <option key={opt.code} value={opt.code}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={td()}>
                      {isSE ? (
                        <select
                          aria-label={`Error code for ${p.productName}`}
                          title={`Error code for ${p.productName}`}
                          value={row.errorCode ?? ''}
                          onChange={(e) =>
                            updateRow(idx, { errorCode: e.target.value || undefined })
                          }
                          style={selectStyle()}
                        >
                          <option value="">Select error...</option>
                          {errorCodes
                            .filter((ec) => ec.isActive)
                            .map((ec) => (
                              <option key={ec.code} value={ec.code}>
                                {ec.label}
                              </option>
                            ))}
                        </select>
                      ) : (
                        <span style={{ color: 'var(--ink-400)' }}>—</span>
                      )}
                    </td>
                    <td style={td()}>
                      <input
                        type="text"
                        value={row.resolutionReason ?? ''}
                        onChange={(e) =>
                          updateRow(idx, { resolutionReason: e.target.value || undefined })
                        }
                        placeholder="Optional row-level note"
                        style={{
                          width: '100%',
                          padding: '4px 8px',
                          border: '1px solid var(--ink-300)',
                          borderRadius: 4,
                          fontSize: 12,
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function th(): React.CSSProperties {
  return {
    padding: '8px 10px',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: 'var(--ink-600)',
    borderBottom: '1px solid var(--surface-border)',
  };
}

function td(): React.CSSProperties {
  return {
    padding: '8px 10px',
    verticalAlign: 'top',
    color: 'var(--ink-900)',
  };
}

function selectStyle(): React.CSSProperties {
  return {
    width: '100%',
    padding: '4px 8px',
    fontSize: 12,
    border: '1px solid var(--ink-300)',
    borderRadius: 4,
    background: 'var(--surface-card)',
  };
}
